import {BlobProvider} from '@react-pdf/renderer';
import {ContentCardHeader} from 'components/ui/ContentCardHeader';
import {GridContainer} from 'components/ui/Grid';
import {format} from "date-fns";
import * as React from 'react';
import {Alert, Button, ButtonGroup, Card, FormGroup, Input, Label, Spinner} from 'reactstrap';
import {userData} from 'util/userData';

import {DataGrid, DataGridSortOrder} from '../../components/ui/DataGrid';
import {FontAwesomeIcon} from '../../components/ui/FontAwesomeIcon';
import {Popover} from '../../components/ui/Popover';
import {Tooltip} from '../../components/ui/Tooltip';
import {HttpRequest} from '../../util/http';
import {StatusTypesEndpoint} from '../../util/http/endpoints';

import {ActionAlertFormContainer} from '../ActionAlertFormContainer';
import {getActionStatusIcon} from '../CoachingFormContainer/util';

import {IStatusTypes} from '../CoachingFormStatus/interfaces';
import {actionFilter, alertDetails, IActionAlertForm, Status, User} from './interfaces';
import {prepareForExport, printExport} from './lib/export';
import {getActionAlertPermissions, getBulkCustomerInfo, getUsers} from './lib/http';
import {compareCustomerName, getListOfIndividualSurveyIds, resolveCustomerName} from './lib/utils';

import {actionFilters} from './mockupData';

const filters = actionFilters;

interface IMyAlertsContainerProps {
  alerts: alertDetails[];
  isLoading: boolean;
  newCreatedAlert: alertDetails;
  filterMyAlerts: boolean
}

interface IMyAlertsContainerState {
  records: alertDetails[]
  filteredRecords: alertDetails[]
  statusTypes: IStatusTypes[]
  statuses: Status[]
  sortBy: keyof alertDetails
  selectedAlertIds: Array<alertDetails['id']>
  sortOrder: DataGridSortOrder
  hiddenProperties: Array<keyof alertDetails>
  actionFilters: actionFilter[]
  users: User[]
  isLoading: boolean
  selectedAlertID?: number
  actionAlertFormOpen: boolean
  exportedAlerts: IActionAlertForm[]
  PDFReady: boolean
  PDFLoading: boolean
  enabledInputs: string[]
  manageActionAlerts: boolean
  hasPreviewBeenLoaded?: boolean
  earliestActionAlertCreationDate: string
  customerInfoList: any[]
  exportLoaded?: boolean
  isCustomerDataFetching: boolean
}

export class MyAlertsContainer extends React.Component<IMyAlertsContainerProps, IMyAlertsContainerState> {

  state: IMyAlertsContainerState = {
    sortBy: 'created',
    records: [],
    statuses: [],
    filteredRecords: [],
    statusTypes: [],
    selectedAlertIds: [],
    sortOrder: 'asc',
    hiddenProperties: ['updated', 'assignedBy'],
    actionFilters: filters,
    isLoading: true,
    isCustomerDataFetching: false,
    actionAlertFormOpen: false,
    users: [],
    exportedAlerts: [],
    PDFReady: false,
    PDFLoading: false,
    enabledInputs: [],
    manageActionAlerts: false,
    earliestActionAlertCreationDate: '',
    customerInfoList: [],
    exportLoaded: true,
  }

  componentWillMount() {
    this.loadStatuses();
    getUsers(userData.getCurrentProject()).then((users) => this.setState({users}));
    getActionAlertPermissions(userData.getCurrentProject(), this.state.enabledInputs).then((enabledInputs) => this.setState({enabledInputs}));
    this.setManageActionAlertsCapability();
  }

  componentDidMount() {
    window.addEventListener('updateRecords', (e: CustomEvent) => {
      if (e.detail !== 'updateAlert') {
        this.setState({actionAlertFormOpen: false}, this.loadStatuses);
      } else {
        this.loadStatuses();
      }
    });
    this.resetFilters();
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.alerts !== prevProps.alerts) {
      this.loadAlerts();
      this.setState({isLoading: this.props.isLoading});
    }

    if (this.props.filterMyAlerts !== prevProps.filterMyAlerts) {
      // this.loadAndFilterRows();
      this.loadAlerts();
    }
  }

  setManageActionAlertsCapability() {
    const currenProject = userData.getCurrentProject();
    userData.getUserId().then((userID) => {
      const method = 'POST';
      const data = {user: userID, project: currenProject};
      HttpRequest.fetch('/scripts/users/get_actionalert_permissions', {data, method})
        .then((res: any) => {
          if (res.data) {
            this.setState({manageActionAlerts: true}, this.loadAlertPreview);
          }
        })
    });
  }

  loadAlerts() {
    const alerts = this.props.alerts;
    if (alerts) {
      let earliestActionAlertCreationDate = '';
      getBulkCustomerInfo(
        userData.getCurrentProject(),
        getListOfIndividualSurveyIds(alerts).join(',')
      ).then((individualSurveysCustomerInfo) => {
        this.setState({customerInfoList: individualSurveysCustomerInfo ? individualSurveysCustomerInfo : []})
        this.setState({isCustomerDataFetching: false})
      })
      alerts.map((alert) => {
        const originalDate = alert.created.slice(0, 10);
        if (earliestActionAlertCreationDate.length === 0 && format(new Date(alert.created.split(' ')[0]), 'MM/dd/yy') !== 'Invalid Date') {
          earliestActionAlertCreationDate = originalDate;
        }
        if (alert.source === 'Basic Alert') {
          alert.isActionAlert = true;
          alert.isWow = false;
          alert.isPerfectScore = false;
          alert.source = (alert.createdBy ? alert.createdBy : alert.source)
        }
        if (alert.source == null) {
          alert.source = 'Basic Alert';
        }
        if (alert.assignedTo == null || alert.assignedTo.length === 0) {
          alert.assignedTo = 'Non specified';
        }
        alert.isSalesOpportunity = (alert.isSalesOpportunity || alert.actionItemReason === 'Sales Opportunity');
        alert.isAttritionRisk = (alert.isAttritionRisk || alert.actionItemReason === 'Attrition Risk');
        alert.dueDateInMS = (alert.dueDate ? new Date(alert.dueDate).getTime() : 0)
        return alert;
      })
      this.setState({records: alerts});
      this.setState({filteredRecords: alerts}, () => this.loadAndFilterRows());
      // this.setState({ isLoading: this.props.isLoading });
      this.selectNoneAlert();
      this.setState({earliestActionAlertCreationDate}, () => {
        const setInitialDate = new CustomEvent('setInitialDate', {detail: this.state.earliestActionAlertCreationDate});
        window.dispatchEvent(setInitialDate)
      });
    }
  }

  loadStatuses() {
    HttpRequest.fetch<IStatusTypes[]>(StatusTypesEndpoint)
      .then((statusTypes) => this.setState({statusTypes}))

    HttpRequest.fetch<Status[]>('/scripts/my_alerts/get_status')
      .then((statuses) => this.setState({statuses}))
  }

  getStatus(value: alertDetails): number {
    const {statuses} = this.state

    const status = statuses.find((st) => (st.alert === value.id))
    return (status) ? status.resolutionId : ((value.isActionAlert === true) ? 0 : 3)
  }

  getStatusName(value: alertDetails): IStatusTypes['status'] {
    const {statusTypes} = this.state
    if (statusTypes.length > 0) {
      const statusId = this.getStatus(value)
      const statusType = this.state.statusTypes.find((st) => (statusId !== undefined && statusId === st.id))

      if (statusType) {
        return statusType.status
      }
    }
    return ''
  }

  exportMyAlertsCSV() {
    const {selectedAlertIds} = this.state
    const alertIdJson = JSON.stringify(selectedAlertIds);
    this.setState({exportLoaded: false});
    const filename = 'my_alerts_' + new Date().toJSON().slice(0, 10) + '.csv'
    $.ajax({
      url: '/scripts/my_alerts/get_my_alerts_csv',
      data: {alertIdJson},
      type: 'post',
      dataType: 'json',
      // tslint:disable-next-line:unnecessary-bind
      success: function(data) {
        const csvContent = data
        const link = window.document.createElement('a');
        link.setAttribute('href', 'data:text/csv;charset=utf-8,%EF%BB%BF' + encodeURI(csvContent));
        link.setAttribute('download', filename);
        link.click();
        this.setState({exportLoaded: true})
      }.bind(this)
    }).then(() => {
      // do nothing else
    })
  }

  isHiddenColumn(prop: keyof alertDetails) {
    return this.state.hiddenProperties.indexOf(prop) >= 0
  }

  toggleHiddenColumn(prop: keyof alertDetails) {
    const {hiddenProperties} = this.state
    const indexOfProp = hiddenProperties.indexOf(prop)

    if (indexOfProp >= 0) {
      hiddenProperties.splice(indexOfProp, 1)
    } else {
      hiddenProperties.push(prop)
    }

    this.setState({hiddenProperties})
  }

  // toggleMyAlerts() {
  //   this.setState(
  //     { filterMyAlerts: !this.state.filterMyAlerts }, () => this.loadAndFilterRows());
  // }

  renderBulkCheck(record: alertDetails) {
    const actionsDisabled = false;
    const isSelected = this.isSelected(record)
    const isNotSelected = !isSelected
    const icon = isSelected ? 'check-square' : 'square'
    const className = (isNotSelected || actionsDisabled) ? 'text-dark' : ''
    const iconElement = (
      <FontAwesomeIcon className={className}
                       verticalAlign="initial"
                       icon={icon}
                       regular={isNotSelected && !actionsDisabled}/>
    )

    if (actionsDisabled) {
      return (<span>{iconElement}</span>)
    }

    return (
      <a href="javascript:void(0)" onClick={() => this.toggleSelectedAlert(record)}>
        {iconElement}
      </a>
    )
  }

  /**
   * Just check if String is Email valid address
   * @param email String to be tested
   */
  isEmail(email) {
    const emailCheckRE = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}])|(([a-zA-Z\-\d]+\.)+[a-zA-Z]{2,}))$/;
    return emailCheckRE.test(email);
  }

  /**
   * renderIdColumn
   */
  renderIdColumn(id) {
    return (
      <Button color="link" className="id-wrapper" onClick={() => {
        this.setState({selectedAlertID: id}, () => {
          this.setState({actionAlertFormOpen: true});
        })
      }}>
        <FontAwesomeIcon icon="search" verticalAlign="initial" size="md"/>
        <span>&nbsp;{id}</span>
      </Button>
    )
  }

  /**
   * Takes a record and returns plain text or link
   * @param record
   * @param destination
   * @returns link/no link to be rendered
   */
  renderAssignationColumn(record: alertDetails, destination: string) {
    // Is it email
    if (this.isEmail(record['assigned' + destination])) {
      return (record['assigned' + destination])
    } else {
      return (record['assigned' + destination])
    }
  }

  /**
   * Returns either string or link to email
   * @param record Alert Detail object
   */
  renderSourceColumn(record: alertDetails) {
    if (this.isEmail(record.source)) {
      return (
        <span>{record.source}</span>
      )
    }
    return (
      <span>{record.source}</span>
    )
  }

  renderStatusColumn(record: alertDetails) {
    const statusName = this.getStatusName(record)
    const isWow = record.isWow;
    const isPerfectScore = record.isPerfectScore;
    const isSalesOpportunity = record.isSalesOpportunity;
    const isAttritionRisk = record.isAttritionRisk;
    let customStyles = '';
    if (!isAttritionRisk) {
      customStyles = customStyles + 'leftAlign';
    }
    if (!isPerfectScore) {
      customStyles = customStyles + ' not-perfect';
    }

    return (
      <article id="status-column" className={customStyles}>
        <Tooltip text={isSalesOpportunity ? 'Sales Opportunity' : 'Not a Sales Opportunity'}>
          <FontAwesomeIcon icon="dollar-sign" color="green" muted={!isSalesOpportunity}/>
        </Tooltip>
        <Tooltip text={isAttritionRisk ? 'Attrition Risk' : 'Not an Attrition Risk'}>
          <FontAwesomeIcon icon="asterisk" color="red" muted={!isAttritionRisk}/>
        </Tooltip>
        <Tooltip text={isWow ? 'WOW' : 'No WOW'}>
          <FontAwesomeIcon icon="star" color="yellow" muted={!isWow}/>
        </Tooltip>
        <Tooltip text={isPerfectScore ? 'Perfect Score' : 'Not a Perfect Score'}>
          <FontAwesomeIcon icon="check" color="green" muted={!isPerfectScore}/>
        </Tooltip>
        <Tooltip text={statusName}>
          {getActionStatusIcon(statusName)}
        </Tooltip>
      </article>
    )
  }

  renderSeverityColumn(record: alertDetails) {
    return (
      <span
        style={{color: (record.severityColor ? record.severityColor : '#6c757d')}}>{(record.severityLabel ? record.severityLabel : 'Low')}</span>
    );
  }

  renderDueInColumn(record: alertDetails) {
    if (record.dueDateInMS > 0 && record.dueDateInMS < 32535129600000) {
      const now = new Date();
      const dueDateObj = new Date(record.dueDate);
      dueDateObj.setHours(23, 59, 59, 999)
      if (now > dueDateObj) {
        return (
          <span className="dueDate text-danger">{dueDateObj.toLocaleDateString()}</span>
        );
      } else {
        return (
          <span className="dueDate">{dueDateObj.toLocaleDateString()}</span>
        );
      }
    } else {
      return <span className="dueDate">No Due Date</span>
    }
  }

  isSelected(record: alertDetails) {
    const {selectedAlertIds} = this.state
    const selectedIndex = selectedAlertIds.indexOf(record.id)

    return selectedIndex >= 0
  }

  toggleSelectedAlert(record: alertDetails) {
    const {selectedAlertIds} = this.state
    const selectedIndex = selectedAlertIds.indexOf(record.id)

    if (selectedIndex >= 0) {
      selectedAlertIds.splice(selectedIndex, 1)
    } else {
      selectedAlertIds.push(record.id)
    }

    this.setState({selectedAlertIds})
  }

  selectAllAlerts() {
    const {filteredRecords} = this.state
    const selectedAlertIds = filteredRecords.map((r) => r.id)

    this.setState({selectedAlertIds})
  }

  selectNoneAlert() {
    this.setState({selectedAlertIds: []})
  }

  toggleFilter(filter) {
    const actionFilters1 = this.state.actionFilters
    const indexFilter = actionFilters1.indexOf(filter)
    const currenStatus = actionFilters1[indexFilter].status
    actionFilters1[indexFilter].status = !currenStatus

    this.setState({actionFilters: actionFilters1})

    this.loadAndFilterRows()
  }

  resetFilters() {
    const actionFilters1 = this.state.actionFilters
    actionFilters1.forEach((filter) => {
      filter.status = !(filter.reason !== 'New' && filter.reason !== 'Pending');
    })
    this.setState({actionFilters: actionFilters1})
    this.loadAndFilterRows()
  }

  loadAndFilterRows(shouldBeInDataGrid?: number) {
    const activeFilters = [];
    let filteredRecords = this.state.records;
    let statusFilter = this.state.statusTypes.reduce((acc, curr) => {
      return acc.concat(curr.status);
    }, []);
    this.state.actionFilters.forEach((filter) => {
      if (filter.status) {
        activeFilters.push(filter.reason)
      }
    });
    statusFilter = statusFilter.filter((stFilter) => activeFilters.includes(stFilter));
    if (activeFilters.length) {
      filteredRecords = this.state.records.filter((record) => {
        const status = this.getStatusName(record)
        const hasWow = record.isWow
        const hasPerfectScore = record.isPerfectScore
        const isSalesOpportunity = record.isSalesOpportunity
        const isAttritionRisk = record.isAttritionRisk

        if (activeFilters.includes('Sales Opportunity') && !isSalesOpportunity) {
          return record.actionItemReason === 'Sales Opportunity';
        }
        if (activeFilters.includes('Attrition Risk') && !isAttritionRisk) {
          return record.actionItemReason === 'Attrition Risk';
        }
        if (activeFilters.includes('WOW') && !hasWow) {
          return false;
        }
        if (activeFilters.includes('Perfect Score') && !hasPerfectScore) {
          return false;
        }
        return !(statusFilter.length > 0 && !statusFilter.includes(status));
      })
    }
    if (this.props.filterMyAlerts) {
      userData.getUserId().then((id) => {
        filteredRecords = filteredRecords.filter((record) => {
          return record.assignedToID === id || record.id === shouldBeInDataGrid;
        });
        this.setState({filteredRecords}, this.loadAlertPreview);
      });
    } else {
      this.setState({filteredRecords}, this.loadAlertPreview);
    }
  }

  loadAlertPreview() {
    const {records, filteredRecords, hasPreviewBeenLoaded} = this.state;
    if (detectIE() > 11 || !detectIE()) {
      const searchParameter = new URLSearchParams(window.location.search);
      if (searchParameter.has('alert_id')) {
        const alertID = parseInt(searchParameter.get('alert_id'), 10);
        const data = {id: alertID};
        const method = 'POST';
        const isAlertForPreviewInDataGrid = filteredRecords.findIndex((item) => item.id === alertID) > -1;
        const isAlertForPreviewInQuery = records.findIndex((item) => item.id === alertID) > -1;
        if (isAlertForPreviewInQuery) {
          if ((isAlertForPreviewInDataGrid || this.state.manageActionAlerts) && !hasPreviewBeenLoaded) {
            this.setState({selectedAlertID: alertID}, () => this.setState({
              actionAlertFormOpen: true,
              hasPreviewBeenLoaded: true
            }, () => this.loadAndFilterRows(alertID)));
          }
        } else {
          HttpRequest.fetch('scripts/my_alerts/get_alert', {data, method})
            .then((res: alertDetails) => {
              res.created = format(new Date(res.created.split(' ')[0]), 'MM/dd/yy');
              res.created = format(new Date(res.created.split(' ')[0]), 'MM/dd/yy');


              records.push(res);
              this.setState({records}, () => this.loadAndFilterRows(res.id));
            })
        }
      }
    }
  }

  toggleModal() {
    const {actionAlertFormOpen} = this.state;
    if (!actionAlertFormOpen) {
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.delete('alert_id');
    }

    this.setState({actionAlertFormOpen: !actionAlertFormOpen}, () => {
      this.setState({selectedAlertID: undefined});
    });
  }

  exportDone(exportedAlerts: IActionAlertForm[]) {
    this.setState({exportedAlerts}, () => {
      this.setState({PDFReady: true, PDFLoading: false});
      this.selectNoneAlert();
    })
  }

  resetPDF() {
    this.setState({PDFReady: false});
  }

  updateFilterTags(filterString: string) {
    const selectedActionFilter = this.state.actionFilters.find((filter) => filter.reason === filterString);
    this.toggleFilter(selectedActionFilter);
  }

  renderCreatedColumn(record: alertDetails): JSX.Element {
    if (record.created) {
      const formattedDate = format(new Date(record.created), 'MM/dd/yy');
      return <span>{formattedDate}</span>;

    } else {
      return <span>n/a</span>
    }
  }

  renderUpdatedColumn(record: alertDetails): JSX.Element {
    if (record.updated) {
      const formattedDate = format(new Date(record.updated), 'MM/dd/yy');
      return <span>{formattedDate}</span>;
    } else {
      return <span>n/a</span>
    }
  }

  renderCustomerInfo(record: alertDetails): JSX.Element {
    const {customerInfoList} = this.state
    return (
      <span>{resolveCustomerName(record, customerInfoList)}</span>
    )
  }

  customSorting(sortBy: string, sortOrder: string, filteredRecords: alertDetails[]): alertDetails[] {
    if (sortBy === 'dueDateInMS') {
      if (sortOrder === 'desc') {
        return filteredRecords.map((alert) => {
          alert.dueDateInMS = (alert.dueDate ? new Date(alert.dueDate).getTime() : 32535129699999)
          return alert
        })
      } else {
        return filteredRecords.map((alert) => {
          alert.dueDateInMS = (alert.dueDate ? new Date(alert.dueDate).getTime() : 0)
          return alert
        })
      }
    }
    if (sortBy === 'customerName') {
      return filteredRecords.sort((a, b) => compareCustomerName(a, b, this.state.customerInfoList, sortOrder))
    }
    return []
  }

  render() {
    const {records, filteredRecords, selectedAlertIds, PDFReady, PDFLoading, exportLoaded} = this.state
    const actionFilters1 = this.state.actionFilters;
    const isLoading = this.props.isLoading;
    const hasNoData = filteredRecords.length === 0 && isLoading === false;
    const hasData = filteredRecords.length > 0;
    const recordsLength = records.length
    const selectedLength = selectedAlertIds.length
    const shouldDisableBulkSelectAll = selectedLength === recordsLength
    const noneSelected = selectedLength <= 0
    const firstRowFilters = actionFilters1.slice(1, 5)
    const secondRowFilters = actionFilters1.slice(5, 10)
    const filterTags = actionFilters1.reduce((acc, curr) => {
      if (curr.status) {
        acc.push(curr.reason);
      }
      return acc;
    }, [])
    return (
      <>
        <section className="blacklight">
          <GridContainer>
            {(filterTags && filterTags.length) > 0 &&
              <div>
                {/*Filter tags*/}
                {filterTags.map((filter, idx) => {
                  return (
                    <Button key={idx} outline color="dark" size="sm" style={{marginRight: '10px', marginBottom: '10px'}}
                            onClick={() => {
                              this.updateFilterTags(filter);
                            }}>{filter} <FontAwesomeIcon icon="times" verticalAlign="initial" size="md"/></Button>
                  );
                })}
              </div>
            }
            <Card<any>>
              <ContentCardHeader
                sticky
                borderBottom
                header={
                  <section className="d-md-flex justify-content-between">
                    <article>
                      <span>{(this.props.filterMyAlerts ? 'MY ALERTS' : 'ALL ALERTS')}</span>
                      {isLoading &&
                        <FontAwesomeIcon icon="cog" size="sm" verticalAlign="middle" spin muted/>
                      }
                      {!noneSelected &&
                        <small>:&nbsp;{selectedLength.toLocaleString()}&nbsp;selected</small>
                      }
                    </article>
                  </section>
                }
                headerActions={
                  <section className="mt-2 d-md-flex justify-content-between">
                    <article>
                      <Button className="mr-2" color="primary"
                              disabled={noneSelected}
                              onClick={() => {
                                prepareForExport(this.state.selectedAlertIds, this.state.users, this.state.statusTypes).then((exportedAlerts: IActionAlertForm[]) => this.exportDone(exportedAlerts));
                                this.setState({PDFLoading: true});
                              }}>
                        Export Selected to PDF &nbsp;
                        {PDFLoading &&
                          <FontAwesomeIcon icon="cog" size="sm" verticalAlign="middle" spin muted/>
                        }
                        {PDFReady &&
                          <>
                            <BlobProvider document={printExport(this.state.exportedAlerts, this.state.enabledInputs)}>
                              {({url, loading}) => {
                                if (!loading) {
                                  window.open(url, '_blank');
                                  setTimeout(() => {
                                    this.resetPDF()
                                  }, 200);
                                  return <p className="d-none">PDF Ready</p>
                                } else {
                                  return <Spinner color="light"/>
                                }
                              }}
                            </BlobProvider>
                          </>
                        }
                      </Button>
                      <Button className="mr-2" color="primary"
                              disabled={noneSelected}
                              onClick={() => this.exportMyAlertsCSV()}
                      >
                        {exportLoaded ? <span>Export Selected to CSV</span> :
                          <Spinner color="white" style={{width: '1.5rem', height: '1.5rem'}}/>}
                      </Button>
                      <Button className="mr-2" color="primary"
                              disabled={shouldDisableBulkSelectAll}
                              onClick={() => this.selectAllAlerts()}>
                        Select All
                      </Button>
                      <Button className="mr-2" color="primary"
                              disabled={noneSelected}
                              onClick={() => this.selectNoneAlert()}>
                        Clear Selection
                      </Button>

                      <Button className="mr-2" color="primary" onClick={() => {
                        this.setState({selectedAlertID: undefined}, () => {
                          this.setState({actionAlertFormOpen: true});
                        })
                      }}>
                        Create New Alert
                      </Button>
                    </article>
                    <article className="d-md-flex justify-content-end">
                      <Popover btnText="Fields">
                        <section style={{fontSize: '1rem', minWidth: 150, maxHeight: '250px', overflowY: 'scroll'}}>
                          <FormGroup check>
                            <Label>
                              <Input type="checkbox" checked={!this.isHiddenColumn('created')}
                                     onChange={() => this.toggleHiddenColumn('created')}/>
                              <span>&nbsp;Created</span>
                            </Label>
                          </FormGroup>
                          <FormGroup check>
                            <Label>
                              <Input type="checkbox" checked={!this.isHiddenColumn('updated')}
                                     onChange={() => this.toggleHiddenColumn('updated')}/>
                              <span>&nbsp;Updated</span>
                            </Label>
                          </FormGroup>
                          <FormGroup check>
                            <Label>
                              <Input type="checkbox" checked={!this.isHiddenColumn('assignedTo')}
                                     onChange={() => this.toggleHiddenColumn('assignedTo')}/>
                              <span>&nbsp;Assigned To</span>
                            </Label>
                          </FormGroup>
                          <FormGroup check>
                            <Label>
                              <Input type="checkbox" checked={!this.isHiddenColumn('assignedBy')}
                                     onChange={() => this.toggleHiddenColumn('assignedBy')}/>
                              <span>&nbsp;Assigned By</span>
                            </Label>
                          </FormGroup>
                          <FormGroup check>
                            <Label>
                              <Input type="checkbox" checked={!this.isHiddenColumn('source')}
                                     onChange={() => this.toggleHiddenColumn('source')}/>
                              <span>&nbsp;Source</span>
                            </Label>
                          </FormGroup>
                          <FormGroup check>
                            <Label>
                              <Input type="checkbox" checked={!this.isHiddenColumn('actionItemReason')}
                                     onChange={() => this.toggleHiddenColumn('actionItemReason')}/>
                              <span>&nbsp;Action Item Reason</span>
                            </Label>
                          </FormGroup>
                          <FormGroup check>
                            <Label>
                              <Input type="checkbox" checked={!this.isHiddenColumn('unit')}
                                     onChange={() => this.toggleHiddenColumn('unit')}/>
                              <span>&nbsp;Team</span>
                            </Label>
                          </FormGroup>
                        </section>
                      </Popover>
                      <Popover className="ml-3" btnText="Action Filters" maxWidth={true} rightAlign={true}>
                        <section className="d-flex justify-content-between">
                          <ButtonGroup className="mb-3">
                            <Button color="white" active={false} onClick={() => this.resetFilters()}>
                              <FontAwesomeIcon icon="ban" fixedWidth style={{color: 'red'}}/>
                              <span>&nbsp;Reset Action Filters</span>
                            </Button>
                          </ButtonGroup>

                        </section>
                        <section className="d-flex justify-content-between">
                          {firstRowFilters.map(function(filter, idx) {
                            return (
                              <ButtonGroup className="mb-3" key={idx}>
                                <Button color="white" active={filter.status} onClick={() => this.toggleFilter(filter)}>
                                  <FontAwesomeIcon icon={filter.icon} fixedWidth style={{color: filter.iconColor}}/>
                                  <span>&nbsp;{filter.reason}</span>
                                </Button>
                              </ButtonGroup>
                            )
                          }, this)}
                        </section>
                        {secondRowFilters.map(function(filter, idx) {
                          return (
                            <ButtonGroup className="mb-3" key={idx}>
                              <Button color="white" active={filter.status} onClick={() => this.toggleFilter(filter)}>
                                <FontAwesomeIcon icon={filter.icon} fixedWidth style={{color: filter.iconColor}}/>
                                <span>&nbsp;{filter.reason}</span>
                              </Button>
                            </ButtonGroup>
                          )
                        }, this)}
                      </Popover>
                    </article>
                  </section>
                }
              />
              {hasNoData &&
                <Alert color="warning" className="animated fadeIn">
                  <FontAwesomeIcon icon="sticky-note" verticalAlign="initial" size="md"/>
                  <span>&nbsp;No data found for the current filters.</span>
                </Alert>
              }
              {hasData &&
                <DataGrid<alertDetails>
                  className="animated fadeIn my-alerts-container"
                  sortBy={this.state.sortBy}
                  sortOrder={this.state.sortOrder}
                  onSortChange={(sortBy, sortOrder) =>
                    this.setState({sortBy, sortOrder}, () => {
                      const updatedRecords = this.customSorting(
                        sortBy,
                        sortOrder,
                        filteredRecords
                      );
                      if (updatedRecords.length > 0) {
                        this.setState({filteredRecords: updatedRecords});
                      }
                    })
                  }
                  records={filteredRecords}
                  columns={[
                    {header: 'Bulk', sortable: false, width: 55, render: (record) => this.renderBulkCheck(record)},
                    {header: 'ID', property: 'id', render: (record) => this.renderIdColumn(record.id)},
                    {
                      header: 'Created',
                      property: 'created',
                      hidden: this.isHiddenColumn('created'),
                      render: (record) => this.renderCreatedColumn(record)
                    },
                    {
                      header: 'Updated',
                      property: 'updated',
                      hidden: this.isHiddenColumn('updated'),
                      render: (record) => this.renderUpdatedColumn(record)
                    },
                    {
                      header: 'Assigned To',
                      property: 'assignedTo',
                      render: (record) => this.renderAssignationColumn(record, 'To'),
                      hidden: this.isHiddenColumn('assignedTo')
                    },
                    {
                      header: 'Customer Name',
                      property: 'customerName',
                      render: (record) => this.renderCustomerInfo(record)
                    },
                    {
                      header: 'Assigned By',
                      property: 'assignedBy',
                      render: (record) => this.renderAssignationColumn(record, 'By'),
                      hidden: this.isHiddenColumn('assignedBy')
                    },
                    {
                      header: 'Source',
                      property: 'source',
                      render: (record) => this.renderSourceColumn(record),
                      hidden: this.isHiddenColumn('source')
                    },
                    {
                      header: 'Action Item Reason',
                      property: 'actionItemReason',
                      hidden: this.isHiddenColumn('actionItemReason')
                    },
                    {header: 'Team', property: 'unit', hidden: this.isHiddenColumn('unit')},
                    {header: 'Status', render: (record) => this.renderStatusColumn(record)},
                    {header: 'Severity', render: (record) => this.renderSeverityColumn(record)},
                    {header: 'Due', property: 'dueDateInMS', render: (record) => this.renderDueInColumn(record)},
                  ]}
                />
              }
            </Card>
          </GridContainer>
        </section>
        <ActionAlertFormContainer
          customer={{name: '', email: '', phoneNumber: '888-888-888'}}
          alertID={this.state.selectedAlertID}
          isOpen={this.state.actionAlertFormOpen}
          toggleModal={() => this.toggleModal()}
        />
      </>
    )
  }

}

/**
 * detect IE
 * returns version of IE or false, if browser is not Internet Explorer
 */
function detectIE() {
  const ua = window.navigator.userAgent;

  const msie = ua.indexOf('MSIE ');
  if (msie > 0) {
    // IE 10 or older => return version number
    return parseInt(ua.substring(msie + 5, ua.indexOf('.', msie)), 10);
  }

  const trident = ua.indexOf('Trident/');
  if (trident > 0) {
    // IE 11 => return version number
    const rv = ua.indexOf('rv:');
    return parseInt(ua.substring(rv + 3, ua.indexOf('.', rv)), 10);
  }

  const edge = ua.indexOf('Edge/');
  if (edge > 0) {
    // Edge (IE 12+) => return version number
    return parseInt(ua.substring(edge + 5, ua.indexOf('.', edge)), 10);
  }

  // other browser
  return false;
}
